const Async = require('async');
const PictProvider = require('pict-provider');

/** @typedef {import('../HeadlightConfigurableApplication.js').Pict} Pict */
/** @typedef {import('./InputTypeBehaviorProvider')} InputTypeBehaviorProvider */

/**
 * @typedef {Object} FieldDistillingMapping
 *
 * @property {String} DatumKey - The data locatinon with in the form.
 * @property {String} BundleHash - The bundle hash to load from the distilling service.
 * @property {String} [BundleNameKey] - (optional) The bundle key that represents the value's name (for display). If omitted, the value is used for name and value.
 * @property {String} BundleValueKey - The bundle key that represents the value to store in the form datum.
 * @property {String} Type - The type of control (used to understand how to marshal data). 'select' is for a select2 dropdown, otherwise it is treated as scalar text.
 * @property {Boolean} [Readonly] - (optional) If the field should be readonly.
 * @property {String} [NoSelectionName] - (optional) The name to use for the "no selection" option.
 * @property {String} [NoSelectionValue] - (optional) The value to use for the "no selection" option.
 */

/**
 * Provider to add behaviors to interface with views / controls based on InputType.
 *
 * Ideas:
 *
 * Understand what kind of values the control can accept? Single- vs. multi-value?
 * Understand whether the control is editable / readonly.
 * Understand whether the control can be auto-filled.
 * Understand whether the control is part of a formula? (ex. in case we need to trigger the solver on distilling?)
 * Understand the control's connection to the distilling bundle.
 * Get / set the control value / selection.
 */
class DistillingProvider extends PictProvider
{
	/**
	 * @param {any} pFable - Fable instance
	 * @param {Object} pOptions - Options for the provider
	 * @param {String} pServiceHash - Hash of the service
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		/**
		 * @type {{
		 *	Interactive: boolean,
		 *	ConditionalCSS: Record<String, String>,
		 *	InitialSaveRequired: boolean,
		 *	Status: string,
		 *	DistillingMapping: Array<FieldDistillingMapping>,
		 *	Select2OptionsCache: Record<String, Array<{ id: string, text: string }>>,
		 *	DocumentData: any,
		 * }}
		 */
		this.AppData;
		// this is set on each index.html for the respective document behavior
		this.AppData.Interactive = window['_InteractiveForm'];
		this.Application = this.pict.PictApplication;

		/** @type {import('fable')} */
		this.fable;

		/** @type {any} */
		this.options;
	}

	/**
	 * Using this top get the right type checking. zzz
	 *
	 * @return {InputTypeBehaviorProvider}
	 */
	get input()
	{
		return this.pict.providers.Input;
	}

	/**
	 * Initialize the provider. Call this before any other methods to provide relevant configuration.
	 *
	 * @private
	 *
	 * @param {Object} reportOptions - The report options.
	 * @param {Object} formManifestDefinition - The form manifest definition.
	 */
	_initializeProvider(reportOptions, formManifestDefinition)
	{
		if (Array.isArray(reportOptions?.DistillingMapping))
		{
			this.AppData.DistillingMapping = reportOptions.DistillingMapping;
		}
		else
		{
			this.AppData.DistillingMapping = this.extractDistillingMappingFromManifest(formManifestDefinition?.Descriptors);
		}
		if (Array.isArray(this.AppData.DistillingMapping))
		{
			// only keep mapping with required values populated
			this.AppData.DistillingMapping = this.AppData.DistillingMapping.filter((mapping) =>
			{
				const isValid = mapping?.BundleValueKey && mapping?.BundleHash && mapping?.DatumKey;
				if (!isValid)
				{
					this.pict.log.error('Invalid distilling mapping; ignoring', { mapping });
				}
				return isValid;
			});
		}
	}

	/**
	 * Initialize an HTML element as a distilling enabled control.
	 *
	 * @param {HTMLElement} input - The input to initialize.
	 * @param {Array<FieldDistillingMapping>} [dataToBundleKeyMappings] - The mappings from form fields to distilling bundle keys.
	 *
	 * @return {boolean} - True if the control was initialized.
	 */
	initializeControl(input, dataToBundleKeyMappings)
	{
		//FIXME: separation of concerns
		if (input instanceof HTMLSelectElement && input.getAttribute('pict-initialized') !== 'true')
		{
			const datumInput = this.input.getInputElementForControlElement(input);
			if (!datumInput)
			{
				this.pict.fable.log.error('No datum input found for control', { input });
				return false;
			}
			const options = { };
			const datumKey = datumInput.getAttribute('data-i-datum');
			if (datumKey && dataToBundleKeyMappings.some((mapping) => mapping.DatumKey == datumKey))
			{
				options.dataAdapter = $.fn.select2.amd.require('select2/data/localPaginationAdapter');
				options.ajax = {}; // necessary for pagination to work
				options.datumKey = datumKey; // attaches to the select2 options cache for local pagination (currently only used by distilling)
			}
			this.pict.log.info(`Initializing select2 for ${datumKey} with distilling`, { input });
			$(input).select2(options);
			input.setAttribute('pict-initialized', 'true');
			return true;
		}
		return false;
	}

	/**
	 * Synthesize the distilling mapping from the form definition.
	 *
	 * @param {Record<String, any> | Array<any>} descriptorMap - The descriptor map.
	 *
	 * @return {Array<FieldDistillingMapping>} - The field level distilling mappings to use.
	 */
	extractDistillingMappingFromManifest(descriptorMap)
	{
		const mapping = [];
		if (!descriptorMap)
		{
			return mapping;
		}

		if (!(typeof descriptorMap === 'object'))
		{
			this.pict.fable.log.error('Invalid descriptor map type', { descriptorMap });
			return mapping;
		}

		for (const [ datumKey, descriptor ] of Object.entries(descriptorMap))
		{
			if (typeof descriptor.PictForm?.DistillingMapping === 'object')
			{
				descriptor.PictForm.DistillingMapping.DatumKey = datumKey;
				// attempting to note here fields that should not be considered selected (readonly)
				descriptor.PictForm.DistillingMapping.Type = descriptor.PictForm?.InputType?.toLowerCase() || descriptor.DataType?.toLowerCase();
				if (!('Readonly' in descriptor.PictForm.DistillingMapping))
				{
					descriptor.PictForm.DistillingMapping.Readonly = descriptor.PictForm.Readonly || descriptor.PictForm.DistillingMapping.Type.toLowerCase() == 'readonly';
				}
				mapping.push(descriptor.PictForm.DistillingMapping);
			}
		}
		return mapping;
	}

	/**
	 * Handle document loaded event.
	 *
	 * @param {Record<String, any>} data - The loaded data.
	 */
	async onDataLoad(data)
	{
		//FIXME: once lifecycle exists for real: await super.onDataLoad(data);
		const doc = data?.DocumentData?.ReportData;

		//FIXME: should be able to work from other data sources, not just documents
		if (!doc)
		{
			this.pict.log.error('No document found for distilling provider', { data });
			return;
		}

		//FIXME: just once?
		this._initializeProvider(doc?.ReportOptions, this.pict.settings.DefaultFormManifest);

		//FIXME: streamline / parallelize this
		/**
		 * @type {Record<String, import('@paviasystems/grafee').Grafee>}
		 */
		this.Graphs = { };
		if (this.AppData.DistillingMapping?.length > 0)
		{
			const bundlesToLoad = Array.from(new Set(this.AppData.DistillingMapping
				.map((mapping) => mapping.BundleHash)
				.filter((bundleHash) => typeof bundleHash === 'string')));
			await Async.eachLimit(bundlesToLoad, 3, (bundleHash, next) =>
			{
				//FIXME: may want to source the project ID from elsewhere in some cases
				const uri = bundleHash ? `Distilling/For/${bundleHash}/Project/${doc.IDProject}` : `Distilling/For/Project/${doc.IDProject}`;
				this.pict.HeadlightRestClient.getJSON(uri, (err, bundleResponse) =>
				{
					if (err)
					{
						if (!(err instanceof Error))
						{
							err = new Error(err);
						}
						return next(err);
					}
					const graph = new window.Grafee(bundleResponse.Bundle);
					this.Graphs[bundleHash] = graph;
					next();
				});
			});
		}
	}

	/**
	 * Use configuration to look up current values for distilling mapped fields.
	 *
	 * @return {Record<String, String>} - The distilled values.
	 */
	getDistilledValues()
	{
		/** @type {Record<string, string>} */
		const snapshot = { };
		if (!this.AppData.DistillingMapping?.length)
		{
			return snapshot;
		}
		const formData = this.AppData.DocumentData?.ReportData?.FormData;
		if (!formData)
		{
			return snapshot;
		}
		for (const { DatumKey: datumKey } of this.AppData.DistillingMapping)
		{
			const value = this.pict.manifest.getValueAtAddress(formData, datumKey);
			snapshot[datumKey] = value;
		}
		return snapshot;
	}

	/**
	 * Wire any ui controls to pull data from the distilling graph, based on configuration.
	 *
	 * @param {Array<FieldDistillingMapping>} dataToBundleKeyMappings - The mappings from form fields to distilling bundle keys.
	 */
	registerDistilling(dataToBundleKeyMappings)
	{
		if (!Array.isArray(dataToBundleKeyMappings))
		{
			return;
		}

		const getSelectedValues = () =>
		{
			const snapshot = this.getDistilledValues();
			const selectedValues = { };
			for (const { DatumKey: datumKey, BundleValueKey: bundleValueKey, Readonly: readonly } of dataToBundleKeyMappings)
			{
				if (readonly)
				{
					// don't put readonly fields in the selected values as they are not editable
					continue;
				}
				if (snapshot[datumKey] !== undefined && snapshot[datumKey] !== null && snapshot[datumKey] !== '')
				{
					selectedValues[bundleValueKey] = snapshot[datumKey];
				}
			}
			return selectedValues;
		};

		/**
		 * Trigger the distilling cascade for a given field mapping.
		 *
		 * TODO: decide how we want to handle autofill then removing the original value
		 *
		 * @param {FieldDistillingMapping} sourceFieldMapping - The source field mapping.
		*/
		const triggerDistillingCascade = (sourceFieldMapping) =>
		{
			//TODO: this should also handle invalidating fields as needed
			for (const fieldMapping of dataToBundleKeyMappings)
			{
				const { BundleHash: bundleHash, DatumKey: datumKey, BundleNameKey: bundleNameKey, BundleValueKey: bundleValueKey, Type: type } = fieldMapping;
				/** @type {import('@paviasystems/grafee').Grafee} */
				const graph = this.Graphs[bundleHash];
				if (!graph || !type || type == 'select' || type == 'option')
				{
					continue;
				}
				const values = graph.listValues(bundleValueKey, getSelectedValues());
				if (values && values.length == 1)
				{
					let nameAndValue;
					if (bundleNameKey)
					{
						nameAndValue = graph.extractJoinedValues(bundleValueKey, values, [ bundleNameKey || bundleValueKey ])[0];
					}
					else
					{
						nameAndValue = { [bundleValueKey]: values[0] };
					}
					//FIXME: how should we approach setting both value and display name? do we need that?
					this.input.setValue(`[data-i-datum="${datumKey}"]`, nameAndValue[bundleNameKey || bundleValueKey]);
				}
			}
		};

		const initValues = getSelectedValues();
		for (const fieldMapping of dataToBundleKeyMappings)
		{
			const {
				BundleHash: bundleHash,
				DatumKey: datumKey,
				BundleNameKey: bundleNameKey,
				BundleValueKey: bundleValueKey,
				Type: type,
				NoSelectionName: noSelectionName = 'Select an option',
				NoSelectionValue: noSelectionValue = fieldMapping.Type == 'numeric' ? '0' :'',
			} = fieldMapping;

			const graph = this.Graphs[bundleHash];
			if (!graph)
			{
				this.pict.fable.log.error(`No data graph found for ${bundleHash} processing distilling mapping for ${datumKey}`, { fieldMapping });
				continue;
			}
			if (!type || type == 'select' || type == 'option')
			{
				const element = document.querySelector(`[data-i-datum="${datumKey}"]`);
				let inputElement;
				let selectElement;
				if (element && element instanceof HTMLInputElement)
				{
					inputElement = element;
					//FIXME: this belongs in the input provider
					selectElement = document.getElementById('SELECT-FOR-' + element.id);
				}
				if (element && element instanceof HTMLSelectElement)
				{
					selectElement = element;
					inputElement = this.input.getInputElementForControlElement(element);
				}
				if (!inputElement || !selectElement || !(selectElement instanceof HTMLSelectElement) || !(inputElement instanceof HTMLInputElement))
				{
					this.pict.log.error('Unable to resolve elements for control', { element });
					continue;
				}
				const $selectElement = $(selectElement);
				const cacheDistilledSelect2OptionsJustInTime = () =>
				{
					const selectedValues = getSelectedValues();
					//FIXME: using search and making this dynamic would be much better
					const values = graph.listValues(bundleValueKey, selectedValues);
					/** @type {Array<Record<String, any>>} */
					let namesAndValues;
					//FIXME: put an option for "no selection" in here??
					if (bundleNameKey)
					{
						namesAndValues = graph.extractJoinedValues(bundleValueKey, values, [ bundleNameKey || bundleValueKey ]);
					}
					else
					{
						// no need to query, but convert to objects so code below is agnostic
						namesAndValues = values.map((value) => ({ [bundleValueKey]: value }));
					}
					/** @type {Array<Array<string>>} */
					const select2Options = namesAndValues.map((nameAndValue) =>
						[
							nameAndValue[bundleValueKey] ?? '',
							nameAndValue[bundleNameKey || bundleValueKey] ?? '(no name)',
						]);
					select2Options.unshift([ noSelectionValue, noSelectionName || 'N/A' ]);
					this.pict.fable.log.info(`Pushing ${namesAndValues.length} values for ${datumKey} from ${bundleNameKey || bundleValueKey} (${bundleValueKey})`);
					this.input.setOptions(`[data-i-datum="${datumKey}"]`, select2Options);
				};
				//FIXME: how to delegate all of this to the InputTypeProvider?
				$selectElement.on('select2:opening', cacheDistilledSelect2OptionsJustInTime);
				$selectElement.on('select2:close', triggerDistillingCascade.bind(this, fieldMapping));

				// set initial select2 state for distilled fields
				$selectElement.empty();
				const emptyOption = new Option(noSelectionName, noSelectionValue, true, false);
				$selectElement.append(emptyOption).trigger('change');

				// on load, we just put one value in there, so we can show the selection - the full list is deferred until interaction
				if (bundleValueKey in initValues)
				{
					const value = initValues[bundleValueKey];
					const nameAndValue = graph.extractJoinedValues(bundleValueKey, [ value ], [ bundleNameKey || bundleValueKey ])[0];
					const newOption = new Option(nameAndValue?.[bundleNameKey || bundleValueKey] || value, value, false, true);
					$selectElement.append(newOption).trigger('change');
				}
			}
			//FIXME: how to support other types of inputs in a sane way?
			else // sane?
			{
				const values = graph.listValues(bundleValueKey, getSelectedValues());
				if (values && values.length == 1)
				{
					this.input.setValue(`[data-i-datum="${datumKey}"]`, values[0]);
				}
			}
		}
	}
/*
	Below are the available events from the parent class (PictProvider):

	onBeforeInitialize()
	{
		if (this.pict.LogNoisiness > 3)
		{
			this.log.trace(`PictProvider [${this.UUID}]::[${this.Hash}] ${this.options.ProviderIdentifier} onBeforeInitialize:`);
		}
		return true;
	}
	onBeforeInitializeAsync(fCallback)
	{
		this.onBeforeInitialize();
		return fCallback();
	}

	onInitialize()
	{

		if (this.pict.LogNoisiness > 3)
		{
			this.log.trace(`PictProvider [${this.UUID}]::[${this.Hash}] ${this.options.ProviderIdentifier} onInitialize:`);
		}
		return true;
	}
	onInitializeAsync(fCallback)
	{
		this.onInitialize();
		return fCallback();
	}

	onAfterInitialize()
	{
		if (this.pict.LogNoisiness > 3)
		{
			this.log.trace(`PictProvider [${this.UUID}]::[${this.Hash}] ${this.options.ProviderIdentifier} onAfterInitialize:`);
		}
		return true;
	}
	onAfterInitializeAsync(fCallback)
	{
		this.onAfterInitialize();
		return fCallback();
	}

	onPreRender()
	{
		if (this.pict.LogNoisiness > 3)
		{
			this.log.trace(`PictProvider [${this.UUID}]::[${this.Hash}] ${this.options.ProviderIdentifier} onPreRender:`);
		}
		return true;
	}
	onPreRenderAsync(fCallback)
	{
		this.onPreRender();
		return fCallback();
	}
	onPreSolve()
	{
		if (this.pict.LogNoisiness > 3)
		{
			this.log.trace(`PictProvider [${this.UUID}]::[${this.Hash}] ${this.options.ProviderIdentifier} onPreSolve:`);
		}
		return true;
	}
	onPreSolveAsync(fCallback)
	{
		this.onPreSolve();
		return fCallback();
	}
*/
}

module.exports = DistillingProvider;
